import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  ADD_OFFICE_VISIT,
  PAGE_HEADER_MENU_OFFICE_VISIT,
  TEXT
} from '@moxie/constants';
import { CrmTable, PageHeaderTitle } from '@shared-components/elements';
import { OfficeVisitDrawer } from './office-visit-drawer';
import { Button, Col, Input, Menu, Row, Space, Spin } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useDispatch } from 'react-redux';
import { officeVisitActions, useAppSelector } from '@crm/core';
import { PlusOutlined, ShareAltOutlined } from '@ant-design/icons';
import { getBranchesByUserContactPermission, officeVisitList } from '@crm/services.api';
import { OfficeVisitDetailsDrawer } from './office-visit-details-drawer';
import { SearchBox } from '../../shared';
import { useAssignedBranch } from '@crm/libs/hooks/contacts';
import { capitalizeFirstLetter } from 'libs/shared/src/functions';
import OfficeVisitUrlShareModal from './office-visit-url-share-modal';
import { OfficeVisitApproveModal } from './office-visit-approve-modal';
import { ViewEnquiryDetailsDrawer } from '@crm/src/pages/enquiries/components/view-enquiry/view-enquiry-details-drawer';
import NoteFormProvider from '@crm/src/shared/note/note-form-provider';
import { OfficeVisitContext } from '@crm/src/pages/office-visit/office-visit-provider';
import moment from 'moment';
import { UserPopoverSearch } from '@crm/src/shared/search';
import Badge from 'antd/es/badge';
import DownOutlined from '@ant-design/icons/DownOutlined';
import RedoOutlined from '@ant-design/icons/RedoOutlined';
import DatePicker from 'antd/es/date-picker';
import Checkbox from 'antd/es/checkbox';
import { UserNameCard } from 'libs/shared/src/crm-modules/shared';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { OfficeVisitQueryFilter } from '@model/crm/office-visit';
import { getAssigneeQueryString, getColumns, getDateQuery } from './utilities';
import { useFilter } from './useFilter';
import { useDebouncedValue } from '@crm/libs/hooks';
import { IPageHeaderMenuProps } from '@model/element.props';
import Text from 'antd/lib/typography/Text';

const { RangePicker } = DatePicker;
const OfficeVisitTableComponent: React.FC = () => {
  const [form] = useForm();
  const [visible, setVisible] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const { selectedBranch, setSelectedBranch } = useContext(OfficeVisitContext);
  const { filterQuery, setFilterQuery } = useFilter();
  const officeVisitStatus = filterQuery?.type !== 'archived' ? capitalizeFirstLetter(filterQuery?.type as string) : 'Unattended';
  const uniqueTableKey = JSON.stringify(filterQuery)

  const {
    drawerIsOpen,
    detailDrawerIsOpen,
    refreshTable,
    loggedInUserBranchId,
    isEnquiryDrawerOpen,
    isApproveModalOpen,
    enquiryId,
  } = useAppSelector((store) => ({
    initialData: store.office_visits.singleData,
    drawerIsOpen: store.office_visits.drawerIsOpen,
    detailDrawerIsOpen: store.office_visits.detailDrawerIsOpen,
    refreshTable: store.office_visits.refresh,
    loggedInUserBranchId: store.auth?.user?.branchId as string,
    companyId: store.auth?.user?.companyId,
    isEnquiryDrawerOpen: store.office_visits?.isEnquiryDrawerOpen,
    isApproveModalOpen: store.office_visits?.isApproveModalOpen,
    enquiryId: store.office_visits?.enquiryId,
  }));

  const { branches: branchList } = useAssignedBranch();

  const dispatch = useDispatch();
  const triggerRefresh = () => {
    dispatch(officeVisitActions.officeVisitTableRefresh());
  };

  const showDrawer = () => {
    setVisible(true);
    dispatch(officeVisitActions.clearOfficeVisitSingleData());
    form.setFields([{
      name: 'dueDateLabel',
      value: 'tomorrow'
    },
    {
      name: 'dueAt',
      value: moment().add('days', 1)
    }
    ]);
  };

  const handleClose = () => {
    form.resetFields();
    setVisible(false);
    setDisabled(true);
    dispatch(officeVisitActions.clearOfficeVisitSingleData());
    dispatch(officeVisitActions.closeOfficeVisitDrawer());
  };
  const handleEnquiryDrawerClose = () => {
    dispatch(officeVisitActions.closeOfficeEnquiryDrawer());
  }
  const handleDetailsDrawerClose = () => {
    // dispatch(themeActions.toggleNavbar(false));
    dispatch(officeVisitActions.closeOfficeVisitDetailsDrawer());
  };

  const getData = (params: Record<string, unknown>) => {
    return officeVisitList(params);
  };

  const setDrawerOpen = (): boolean => visible || drawerIsOpen!;

  const setDetailDrawerOpen = () => {
    dispatch(officeVisitActions.openOfficeVisitDetailsDrawer());
  }

  const filterString = JSON.stringify(filterQuery);
  const modifiedFilter = React.useMemo(() => {
    const filterQuery = JSON.parse(filterString) as OfficeVisitQueryFilter;
    const filter = filterQuery?.filter;
    if (filterQuery?.type === "pending") {
      delete filterQuery?.filter?.assigneeId
    }
    return {
      ...filter,
      branchId: selectedBranch,
      assigneeId: getAssigneeQueryString(filter?.assigneeId, filter?.uncheckedAssignee),
      createdAt: getDateQuery(filter?.createdAt),
      search: filterQuery?.search,
    }
  }, [selectedBranch, filterString])

  useEffect(() => {
    if (branchList.length > 0 && branchList.find(b => b.id === loggedInUserBranchId)) {
      const preSelectedBranch = filterQuery?.filter?.branchId ?? '';
      if (preSelectedBranch) {
        setSelectedBranch(preSelectedBranch);
      } else {
        setSelectedBranch(loggedInUserBranchId);
      }
    }
  }, [branchList, loggedInUserBranchId]);

  if (!selectedBranch) {
    return (
      <div className="page-load">
        <Spin />
      </div>
    )
  }

  return (
    <div className="full-height">
      <PageHeaderTitle title={TEXT.OFFICE_VISIT} />
      <PageHeaderMenu
        menu={PAGE_HEADER_MENU_OFFICE_VISIT}
        defaultActiveKey={filterQuery?.type}
        setFilterQuery={setFilterQuery}
        key={filterQuery?.type}
      >
        <div className="add-office-visit-btn-container">
          <Space direction='horizontal'>
            <Button type="ghost" onClick={() => setShowShareModal(true)}><ShareAltOutlined />Share</Button>
            <Button type="primary" onClick={showDrawer}>
              <PlusOutlined data-testid="crm-addofficevisitbutton" />{' '}
              {ADD_OFFICE_VISIT}
            </Button>
          </Space>
        </div>
      </PageHeaderMenu>
      <OfficeVisitDrawer
        visible={setDrawerOpen()}
        onClose={handleClose}
        disabled={disabled}
        setDisabled={setDisabled}
        form={form}
      />
      <NoteFormProvider>
        <OfficeVisitDetailsDrawer
          visible={detailDrawerIsOpen!}
          onClose={handleDetailsDrawerClose}
          setDrawerOpen={setDetailDrawerOpen}
        />
      </NoteFormProvider>
      <div className="bg-white padding-top-2">
        <CrmTable
          tableName={TEXT.OFFICE_VISIT}
          action={getData as any}
          filter={modifiedFilter}
          refresh={refreshTable}
          type={officeVisitStatus ?? 'Pending'}
          columns={getColumns(filterQuery?.type || "pending")}
          className="lead-table"
          hideSearch
          storeStateInUrl={false}
          key={uniqueTableKey}
          tableButtons={
            <>
              <FilterButtons
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                filterQuery={filterQuery}
                setFilterQuery={setFilterQuery}
              />
            </>
          }
        />
      </div>
      <OfficeVisitUrlShareModal key={selectedBranch} showShareModal={showShareModal} onCancel={() => setShowShareModal(false)} selectedBranch={selectedBranch} />
      {enquiryId ?
        <ViewEnquiryDetailsDrawer key={enquiryId} visible={isEnquiryDrawerOpen} handleClose={handleEnquiryDrawerClose} enquiryId={enquiryId} />
        : null}
      <OfficeVisitApproveModal visible={isApproveModalOpen} dispatch={dispatch} triggerRefresh={triggerRefresh} />
    </div>
  );
};

const FilterButtons = ({
  selectedBranch,
  setSelectedBranch,
  filterQuery,
  setFilterQuery,
}: {
  selectedBranch?: string;
  // eslint-disable-next-line no-unused-vars
  setSelectedBranch: (val: string) => void;
  filterQuery: OfficeVisitQueryFilter | null;
  setFilterQuery: React.Dispatch<React.SetStateAction<OfficeVisitQueryFilter | null>>;
}) => {
  const { removeFilterFromStorage } = useFilter();
  const filter = filterQuery?.filter;
  const [search, setSearch] = useState<string | undefined>(filterQuery?.search);
  const [checkAll, setCheckAll] = useState(filterQuery?.filter?.assigneeId?.includes('all') ?? false);
  const uncheckedAssignees = filterQuery?.filter?.uncheckedAssignee ?? [];
  const clearFilter = () => {
    setCheckAll(false);
    setFilterQuery(null);
    removeFilterFromStorage();
    setSearch(undefined);
  };
  const handleBranchChange = (branchId: string) => {
    setSelectedBranch(branchId);
    setFilterQuery(prev => ({ ...prev, filter: { ...prev?.filter, branchId } }));
  };
  const resetUserPopOver = () =>
    setFilterQuery((prev) => ({ ...prev, filter: { ...prev?.filter, assigneeId: [] } }));
  const rangeDateValue = useMemo(
    () =>
      filter?.createdAt
        ? [moment(filter?.createdAt?.[0]), moment(filter?.createdAt?.[1])]
        : [null, null],
    [filter?.createdAt],
  ) as [moment.Moment, moment.Moment] | [null, null];
  const badgeCount = filter?.assigneeId?.includes('all') ? "10+" : (filter?.assigneeId && filter?.assigneeId?.length > 10 ? "10+" : filter?.assigneeId?.length);
  const debouncedSearch = useDebouncedValue(search, 500);

  const filterQueryString = JSON.stringify(filterQuery)
  useEffect(() => {
    if (filterQueryString !== "null") {
      setFilterQuery((prev) => ({ ...prev, search: debouncedSearch }));
    }
  }, [debouncedSearch, setFilterQuery, filterQueryString])
  const showAssigneeButton = !(filterQuery?.type === "pending" || !filterQuery?.type);

  return (
    <div className="display-flex gap-8px">
      <div>
        <Input.Search
          value={search}
          placeholder="Search"
          onChange={e => setSearch(e.target.value)}
          allowClear
        />
      </div>
      <div>
        <SearchBox
          fetchQuery={getBranchesByUserContactPermission}
          valueKey='id'
          labelKey='name'
          value={selectedBranch}
          className="branch-list"
          defaultOpen={false}
          showArrow
          placeholder="Select branch"
          onChange={handleBranchChange}
        />
      </div>
      {showAssigneeButton ? (
        <div>
          <UserPopoverSearch
            isMultiSelect
            assignees={filter?.assigneeId ?? []}
            onReset={resetUserPopOver}
            onChange={(assigneeId, uncheckedAssignee) => {
              setFilterQuery((prev) => ({ ...prev, filter: { ...prev?.filter, assigneeId: assigneeId as string[] } }));
              if (Array.isArray(assigneeId) && assigneeId.includes('all')) {
                setFilterQuery((prev) => ({ ...prev, filter: { ...prev?.filter, assigneeId: ['all'], uncheckedAssignee } }));
              } else if (Array.isArray(assigneeId)) {
                setFilterQuery((prev) => ({ ...prev, filter: { ...prev?.filter, assigneeId, uncheckedAssignee: undefined } }));
              }
            }}
            prependedItems={PrependedItems}
            branchId={selectedBranch}
            checkAll={checkAll}
            setCheckAll={setCheckAll}
            uncheckedAssignees={uncheckedAssignees}
          >
            <Button
              type="text"
              icon={<Badge className="table-filter__badge" count={badgeCount} style={{ marginRight: '10px' }} />}
            >
              Assignee <DownOutlined />
            </Button>
          </UserPopoverSearch>
        </div>
      ) : null}
      <div>
        <RangePicker
          placeholder={['From', 'To']}
          onChange={(value) => {
            if (value) {
              setFilterQuery((prev: any) => ({ ...prev, filter: { ...prev?.filter, createdAt: [value?.[0], value?.[1]] } }));
            } else { // when date is cleared
              setFilterQuery((prev) => ({ ...prev, filter: { ...prev?.filter, createdAt: undefined } }));
            }
          }}
          value={rangeDateValue}
        />
      </div>
      <div className="padding-right-1">
        <Button
          onClick={clearFilter}
          className="enquiry__filter-btn"
          icon={<RedoOutlined />}
          type="link"
        >
          <Space size={8}>
            <span>{TEXT.CLEAR_FILTERS}</span>
          </Space>
        </Button>
      </div>
    </div>
  )
}

const assignedStyle = { paddingBlock: "0.5rem" };
const selectAllStyle = { paddingTop: "0.5rem", paddingBottom: "1rem" };
const PrependedItems = ({
  assigneeMap,
  handleChange,
  checkAll,
  setCheckAll,
  uncheckedAssignees,
}: {
  assigneeMap: Map<string, string>,
  // eslint-disable-next-line no-unused-vars
  handleChange: (e: CheckboxChangeEvent, value: string) => void,
  checkAll?: boolean,
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>,
  uncheckedAssignees?: string[],
}) => {
  const userId = useAppSelector(state => state.auth.user?.id ?? '');
  return (
    <>
      <div style={assignedStyle}>
        <Checkbox
          checked={(checkAll && !uncheckedAssignees?.includes(userId)) || assigneeMap.has(userId)}
          onChange={(e) => handleChange(e, userId)}
        >
          Assigned To Me
        </Checkbox>
      </div>
      <div style={selectAllStyle}>
        <Checkbox
          checked={checkAll}
          onChange={(e) => {
            setCheckAll && setCheckAll((prev: boolean) => !prev);
            handleChange(e, 'all');
          }}
        >
          Select All
        </Checkbox>
      </div>
      <div>
        <Checkbox
          checked={(checkAll && !uncheckedAssignees?.includes('null')) || assigneeMap.has('null')}
          onChange={(e) => handleChange(e, 'null')}>
          <UserNameCard
            firstName={"Unassigned"}
            lastName={""}
            email={""}
          />
        </Checkbox>
      </div>
    </>
  )
}

const PageHeaderMenu: React.FC<IPageHeaderMenuProps & { setFilterQuery: React.Dispatch<React.SetStateAction<OfficeVisitQueryFilter | null>> }> = ({
  menu,
  children,
  defaultActiveKey,
  setFilterQuery,
}) => {
  const [current, setCurrent] = useState(defaultActiveKey || menu[0].id);
  const handleChange = (e: any) => {
    setCurrent(e?.key);
    setFilterQuery(prev => ({ ...prev, type: e?.key }));
  };

  return (
    <Row className="page-header">
      <Col className="page-header__menu">
        <Menu onClick={handleChange} selectedKeys={[current]} mode="horizontal">
          {(menu || []).map((menuItem) => {
            return (
              <Menu.Item key={menuItem.id}>
                <Text strong>{menuItem.name}</Text>
              </Menu.Item>
            );
          })}
        </Menu>
      </Col>
      <Col className="page-header__children">{children && children}</Col>
    </Row>
  );
};

export { OfficeVisitTableComponent };
